<template>
  <div v-if="!battle" class="mb-empty">
    <b>Матч не выбран</b>
    Выберите матч во вкладке «Матчи по ID» — данные появятся здесь и будут обновляться сами.
  </div>

  <div v-else class="mb-data mb-stack">
    <!-- live-сводка -->
    <div class="mb-data__score">
      <div v-for="c in camps" :key="c.camp" :class="['mb-data__team', 'mb-data__team--' + c.camp]">
        <img v-if="campLogoUrl(c)" class="mb-data__logo" :src="campLogoUrl(c)" alt="">
        <span v-else class="mb-data__logo mb-data__logo--ph">{{ campShortName(c).slice(0, 2) }}</span>
        <div class="mb-data__team-name">
          <b class="mb-ellipsis" :title="campTeamName(c)">{{ campTeamName(c) }}</b>
          <span class="mb-muted mb-small">
            {{ c.camp === 1 ? 'синяя сторона' : 'красная сторона' }}
            <template v-if="!c.team"> · название из игры</template>
            <template v-if="seriesWins(c) !== null"> · серия {{ seriesWins(c) }}</template>
          </span>
        </div>
        <span class="mb-data__kills">{{ num(c.score) }}</span>
      </div>

      <div class="mb-data__center">
        <span :class="['mb-badge', 'mb-badge--' + stateVariant(battle.state)]">{{ stateLabel(battle.state) }}</span>
        <b class="mb-data__timer">{{ centerTime }}</b>
        <span class="mb-muted mb-small">{{ centerHint }}</span>
        <span v-if="battle.paused || battle.banpick_paused" class="mb-badge mb-badge--warning">пауза</span>
        <span v-if="battle.state === 'end' && battle.win_camp" class="mb-badge mb-badge--success">
          победа: {{ campTeamName(campByNumber(battle, battle.win_camp)) }}
        </span>
      </div>
    </div>

    <div class="mb-data__grid">
      <!-- объекты карты -->
      <section class="mb-panel">
        <header class="mb-panel__head"><h3>Статистика сторон</h3></header>
        <div class="mb-panel__body mb-panel__body--flush">
          <table class="mb-table mb-table--compact">
            <thead>
            <tr>
              <th></th>
              <th class="mb-num"><span class="mb-badge mb-badge--blue">синие</span></th>
              <th class="mb-num"><span class="mb-badge mb-badge--red">красные</span></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="row in campStats" :key="row.key">
              <td>{{ row.label }}<span v-if="row.postOnly && !battle.post_synced_at" class="mb-muted mb-small"> · после итога</span></td>
              <td class="mb-num">{{ row.blue }}</td>
              <td class="mb-num">{{ row.red }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- live-данные -->
      <section class="mb-panel">
        <header class="mb-panel__head"><h3>Live-данные</h3></header>
        <div class="mb-panel__body">
          <dl class="mb-kv">
            <dt>Игровое время</dt>
            <dd>{{ formatGameTime(battle.game_time) }}</dd>
            <dt>До конца фазы</dt>
            <dd>{{ draftState ? formatGameTime(battle.state_left_time) : '—' }}</dd>
            <dt>Лорд через</dt>
            <dd>{{ battle.lord_left_time > 0 ? formatGameTime(battle.lord_left_time) : 'доступен / —' }}</dd>
            <dt>Черепаха через</dt>
            <dd>{{ battle.tortoise_left_time > 0 ? formatGameTime(battle.tortoise_left_time) : 'доступна / —' }}</dd>
            <dt>Разница золота</dt>
            <dd>{{ goldDiffText }}</dd>
            <dt>Золото за лорда (30 сек)</dt>
            <dd>{{ num(battle.kill_lord_money_advantage) }}</dd>
            <dt>Пауза</dt>
            <dd>{{ battle.paused ? 'в игре' : battle.banpick_paused ? 'в драфте' : 'нет' }}</dd>
            <dt>Итог игры</dt>
            <dd>{{ battle.post_synced_at ? 'получен ' + timeAgo(battle.post_synced_at, now) : 'ещё нет' }}</dd>
            <dt>Патч / режим</dt>
            <dd>{{ battle.game_patch || '—' }} · {{ battle.play_mode_id != null ? battle.play_mode_id : '—' }}</dd>
            <dt>Матч BIGPLAY</dt>
            <dd>{{ battle.group_id ? '#' + battle.group_id : 'не привязан' }}</dd>
          </dl>
        </div>
      </section>

      <!-- драфт -->
      <section class="mb-panel">
        <header class="mb-panel__head"><h3>Драфт</h3></header>
        <div class="mb-panel__body mb-stack">
          <div v-for="c in camps" :key="c.camp" class="mb-draft">
            <span :class="['mb-side', 'mb-side--' + c.camp]"></span>
            <span class="mb-draft__team mb-ellipsis">{{ campShortName(c) }}</span>
            <div class="mb-draft__rows">
              <div class="mb-draft__row">
                <span class="mb-muted mb-small">баны</span>
                <MbHero v-for="(h, i) in padded(campDraftHeroes(battle, c.camp, 'ban'))" :key="'b' + i" :hero="h && h.hero" small icon-only class="is-ban"/>
              </div>
              <div class="mb-draft__row">
                <span class="mb-muted mb-small">пики</span>
                <MbHero v-for="(h, i) in padded(campDraftHeroes(battle, c.camp, 'pick'))" :key="'p' + i" :hero="h && h.hero" small icon-only/>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- перевес по ходу игры (timeline) -->
    <section class="mb-panel">
      <header class="mb-panel__head">
        <h3>Перевес по ходу игры</h3>
        <span class="mb-muted mb-small">синие минус красные</span>
        <span class="mb-spacer"></span>
        <div class="mb-data__metrics">
          <button v-for="m in timelineMetrics"
                  :key="m.key"
                  type="button"
                  :class="['mb-badge', metric === m.key ? 'mb-badge--blue' : 'mb-badge--dark']"
                  @click="metric = m.key">{{ m.label }}
          </button>
        </div>
      </header>
      <div v-if="timeline.length < 2" class="mb-empty">График появится, когда начнётся игра</div>
      <div v-else class="mb-panel__body">
        <apexchart type="area" height="240" :key="'tl' + themeKey" :options="chartOptions" :series="chartSeries"/>
      </div>
    </section>

    <!-- игроки -->
    <section class="mb-panel">
      <header class="mb-panel__head">
        <h3>Игроки</h3>
        <span class="mb-muted mb-small">обновлено {{ timeAgo(battle.live_synced_at, now) }}</span>
        <span class="mb-spacer"></span>
        <span v-if="!battle.post_synced_at" class="mb-muted mb-small">MVP и рейтинг появятся после итога игры</span>
      </header>
      <div class="mb-table-wrap">
        <table class="mb-table mb-table--compact mb-players">
          <thead>
          <tr>
            <th>Игрок</th>
            <th>Герой</th>
            <th class="mb-num">Ур.</th>
            <th class="mb-num">K / D / A</th>
            <th class="mb-num">Золото</th>
            <th class="mb-num">Урон</th>
            <th class="mb-num">Получено</th>
            <th class="mb-num">По строениям</th>
            <th class="mb-num">Рейтинг</th>
            <th>Статус</th>
          </tr>
          </thead>
          <tbody v-for="c in camps" :key="c.camp">
          <tr v-for="p in campPlayers(battle, c.camp)" :key="p.id || p.role_id">
            <td class="mb-players__name">
              <span :class="['mb-side', 'mb-side--' + c.camp]"></span>
              <span class="mb-players__nick">
                <b class="mb-ellipsis" :title="playerName(p).name">{{ playerName(p).name }}</b>
                <span class="mb-muted mb-small mb-ellipsis">
                  <template v-if="playerName(p).linked">ник в игре {{ p.name }}</template>
                  <template v-else><span class="mb-players__nolink">нет профиля BIGPLAY</span> · ник из игры</template>
                </span>
              </span>
            </td>
            <td><MbHero :hero="playerHero(p)" small/></td>
            <td class="mb-num">{{ num(p.level) }}</td>
            <td class="mb-num">{{ playerKda(p) }}</td>
            <td class="mb-num">{{ num(p.gold) }}</td>
            <td class="mb-num">{{ num(p.total_damage) }}</td>
            <td class="mb-num">{{ num(p.total_hurt) }}</td>
            <td class="mb-num">{{ num(p.total_damage_tower) }}</td>
            <td class="mb-num">
              <template v-if="battle.post_synced_at">{{ num(p.score) }}</template>
              <template v-else>—</template>
              <span v-if="p.is_mvp" class="mb-badge mb-badge--warning">MVP</span>
            </td>
            <td>
              <span v-if="p.is_dead" class="mb-badge mb-badge--danger">мёртв · {{ num(p.revive_left_time) }} с</span>
              <span v-else-if="p.banning" class="mb-badge mb-badge--warning">банит</span>
              <span v-else-if="p.picking" class="mb-badge mb-badge--warning">выбирает</span>
              <span v-else-if="battle.state === 'play'" class="mb-badge mb-badge--success">жив</span>
              <span v-else class="mb-muted">—</span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- лента событий -->
    <section class="mb-panel">
      <header class="mb-panel__head">
        <h3>Лента событий</h3>
        <span class="mb-muted mb-small">{{ events.length }} шт.</span>
      </header>
      <div v-if="!feed.length" class="mb-empty">Событий пока нет</div>
      <ul v-else class="mb-feed">
        <li v-for="item in feed" :key="item.key">
          <span class="mb-feed__time mb-mono">{{ formatGameTime(item.game_time) }}</span>
          <span :class="['mb-side', 'mb-side--' + (item.camp || 1), {'is-neutral': !item.camp}]"></span>
          <span class="mb-feed__text">{{ item.text }}</span>
          <span v-for="badge in item.badges" :key="badge" class="mb-badge mb-badge--warning">{{ badge }}</span>
        </li>
      </ul>
    </section>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import MbHero from '@/components/MlbbBroadcast/MbHero.vue'
import {
  campByNumber,
  campLogoUrl,
  campPlayers,
  campShortName,
  campTeamName,
  describeEvent,
  formatGameTime,
  formatNumber,
  campDraftHeroes,
  playerHero,
  playerKda,
  playerName,
  seriesScore,
  stateLabel,
  stateVariant,
  timeAgo,
} from '@/common/mlbbBroadcast'

// Ленту показываем с конца: длинная игра даёт сотни событий, а режиссёру нужны последние
const FEED_LIMIT = 150

const CAMP_STATS = [
  {key: 'score', label: 'Киллы'},
  {key: 'kill_tower', label: 'Башни'},
  {key: 'kill_tortoise', label: 'Черепахи'},
  {key: 'kill_lord', label: 'Лорды'},
  {key: 'total_money', label: 'Золото'},
  {key: 'total_damage', label: 'Урон', postOnly: true},
  {key: 'red_buff_num', label: 'Красный бафф', postOnly: true},
  {key: 'blue_buff_num', label: 'Синий бафф', postOnly: true},
  {key: 'crab_kill_num', label: 'Крабы', postOnly: true},
]

// Метрики графика — поля diff из timeline. Опыт не берём: в точках из WS-тика его нет
const TIMELINE_METRICS = [
  {key: 'gold', label: 'Золото'},
  {key: 'kills', label: 'Киллы'},
  {key: 'towers', label: 'Башни'},
]

const SIDE_COLORS = {light: ['#3b4cf0', '#e0414f'], dark: ['#6f7cff', '#ff6b78']}

export default {
  name: 'MbDataTab',
  components: {MbHero, apexchart: VueApexCharts},
  props: {
    battle: {type: Object, default: null},
    events: {type: Array, default: () => []},
    // точки GET /timeline + дописанные из тиков mlbb-battle.updated
    timeline: {type: Array, default: () => []},
    // все игры серии (если battle привязан к матчу) — для счёта серии
    series: {type: Array, default: () => []},
    now: {type: Number, default: () => Date.now()},
  },
  data() {
    return {
      timelineMetrics: TIMELINE_METRICS,
      metric: 'gold',
      isDark: false,
      themeObserver: null,
    }
  },
  computed: {
    themeKey() {
      return this.isDark ? 'dark' : 'light'
    },
    // Перевес рисуем двумя областями от нуля: выше — синие, ниже — красные
    chartSeries() {
      const blue = []
      const red = []
      this.timeline.forEach((p) => {
        const value = Number(p.diff?.[this.metric]) || 0
        blue.push([p.game_time, Math.max(value, 0)])
        red.push([p.game_time, Math.min(value, 0)])
      })
      return [{name: 'Синие впереди', data: blue}, {name: 'Красные впереди', data: red}]
    },
    chartOptions() {
      const text = this.isDark ? '#98a6ad' : '#7b8496'
      const grid = this.isDark ? '#374558' : '#e7eaf3'
      return {
        chart: {type: 'area', toolbar: {show: false}, fontFamily: 'inherit', background: 'transparent', animations: {enabled: false}},
        theme: {mode: this.themeKey},
        colors: SIDE_COLORS[this.themeKey],
        dataLabels: {enabled: false},
        stroke: {curve: 'straight', width: 2},
        fill: {type: 'solid', opacity: 0.18},
        markers: {size: 0},
        legend: {show: false},
        xaxis: {
          type: 'numeric',
          tickAmount: 8,
          labels: {formatter: (v) => formatGameTime(Math.round(v)), style: {colors: text, fontSize: '11px'}},
          axisBorder: {show: false},
          axisTicks: {show: false},
          tooltip: {enabled: false},
        },
        yaxis: {
          forceNiceScale: true,
          labels: {formatter: (v) => formatNumber(Math.abs(Math.round(v))), style: {colors: text, fontSize: '11px'}},
        },
        annotations: {yaxis: [{y: 0, borderColor: grid, strokeDashArray: 0}]},
        grid: {borderColor: grid, strokeDashArray: 4, padding: {left: 4, right: 4}},
        tooltip: {
          shared: true,
          intersect: false,
          theme: this.themeKey,
          // одна строка вместо двух: обе серии в точке описывают один перевес
          custom: ({dataPointIndex}) => {
            const p = this.timeline[dataPointIndex]
            if (!p) return ''
            const value = Number(p.diff?.[this.metric]) || 0
            const lead = value === 0 ? 'равно' : `${value > 0 ? 'синие' : 'красные'} +${formatNumber(Math.abs(value))}`
            return `<div class="mb-data__tip"><b>${formatGameTime(p.game_time)}</b> ${lead}</div>`
          },
        },
      }
    },
    camps() {
      return [1, 2].map((n) => campByNumber(this.battle, n) || {camp: n})
    },
    draftState() {
      return ['ban', 'pick', 'adjust'].includes(this.battle?.state)
    },
    centerTime() {
      return formatGameTime(this.draftState ? this.battle.state_left_time : this.battle.game_time)
    },
    centerHint() {
      if (this.draftState) return 'до конца фазы'
      if (this.battle.state === 'init') return 'ожидание драфта'
      return 'игровое время'
    },
    goldDiffText() {
      const [b, r] = this.camps
      if (b.total_money == null || r.total_money == null) return '—'
      const diff = Number(b.total_money) - Number(r.total_money)
      if (diff === 0) return '0'
      return `${diff > 0 ? 'синие' : 'красные'} +${formatNumber(Math.abs(diff))}`
    },
    campStats() {
      const [b, r] = this.camps
      return CAMP_STATS.map((row) => ({...row, blue: formatNumber(b[row.key]), red: formatNumber(r[row.key])}))
    },
    score() {
      return this.series.length ? seriesScore(this.series) : null
    },
    feed() {
      return this.events.slice(-FEED_LIMIT).reverse().map((e, i) => ({
        key: e.id != null ? e.id : `${e.event_type}-${e.game_time}-${i}`,
        game_time: e.game_time,
        ...describeEvent(e, this.battle),
      }))
    },
  },
  mounted() {
    this.detectTheme()
    this.themeObserver = new MutationObserver(this.detectTheme)
    this.themeObserver.observe(document.body, {attributes: true, attributeFilter: ['class']})
  },
  beforeDestroy() {
    if (this.themeObserver) this.themeObserver.disconnect()
  },
  methods: {
    detectTheme() {
      this.isDark = document.body.classList.contains('dark-only')
    },
    campByNumber,
    campLogoUrl,
    campPlayers,
    campShortName,
    campTeamName,
    formatGameTime,
    campDraftHeroes,
    playerHero,
    playerKda,
    playerName,
    stateLabel,
    stateVariant,
    timeAgo,
    num: formatNumber,
    padded(list) {
      const arr = (list || []).slice(0, 5)
      while (arr.length < 5) arr.push(null)
      return arr
    },
    seriesWins(camp) {
      if (!this.score || !camp.group_team_id) return null
      return this.score[camp.group_team_id] || 0
    },
  },
}
</script>

<style lang="scss">
.mb-data {
  &__score {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: 16px;
    padding: 16px 18px;
    border: 1px solid var(--tl-border);
    border-radius: var(--tl-radius);
    background: var(--tl-bg);
  }

  &__team {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;

    &--2 { flex-direction: row-reverse; text-align: right; }
  }

  &__center {
    grid-column: 2;
    grid-row: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  &__team--2 { grid-column: 3; grid-row: 1; }

  &__logo {
    flex: none;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    object-fit: contain;
    background: var(--tl-soft);

    &--ph {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-weight: 700;
      background: var(--mb-blue);
    }
  }

  &__team--2 &__logo--ph { background: var(--mb-red); }

  &__team-name {
    display: flex;
    flex-direction: column;
    min-width: 0;

    b { font-size: 16px; }
  }

  &__kills {
    margin-left: auto;
    font-size: 34px;
    font-weight: 700;
    color: var(--mb-blue);
    font-variant-numeric: tabular-nums;
  }

  &__team--2 &__kills { margin-left: 0; margin-right: auto; color: var(--mb-red); }

  &__timer { font-size: 28px; font-variant-numeric: tabular-nums; line-height: 1.1; }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    align-items: start;

    @media (max-width: 1200px) { grid-template-columns: 1fr 1fr; }
    @media (max-width: 760px) { grid-template-columns: 1fr; }
  }

  &__metrics {
    display: flex;
    gap: 4px;

    > button { border: 0; cursor: pointer; }
  }

  &__tip { padding: 6px 10px; font-size: 12px; }
}

.mb-kv {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px 16px;
  margin: 0;
  font-size: 12.5px;

  dt { font-weight: 400; color: var(--tl-muted); }
  dd { margin: 0; text-align: right; font-variant-numeric: tabular-nums; }
}

.mb-draft {
  display: flex;
  align-items: center;
  gap: 10px;

  .mb-side { height: 52px; }
  &__team { flex: 0 0 70px; font-weight: 600; font-size: 12.5px; }
  &__rows { display: flex; flex-direction: column; gap: 6px; }
  &__row { display: flex; align-items: center; gap: 4px; }
  &__row > .mb-muted { width: 34px; }

  .is-ban { filter: grayscale(.8); opacity: .75; }
}

.mb-players {
  &__name { display: flex; align-items: center; gap: 10px; min-width: 180px; max-width: 260px; }
  &__nick { display: flex; flex-direction: column; min-width: 0; }
  &__nolink { color: #b7740a; }

  tbody + tbody tr:first-child td { border-top: 2px solid var(--tl-border); }
}

.mb-feed {
  max-height: 420px;
  margin: 0;
  padding: 6px 0;
  overflow-y: auto;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 18px;
    font-size: 13px;
  }

  li + li { border-top: 1px solid var(--tl-border); }

  &__time { flex: none; width: 46px; color: var(--tl-muted); }
  &__text { min-width: 0; }

  .mb-side.is-neutral { background: var(--tl-border); }
}
</style>
